import { filesize } from 'humanize-plus';
import { useToast } from '@@/packages/shared/components/toast/useToast';
import { formatList } from '@@/packages/shared/libs/text';
import type { FileModel } from '@@/packages/shared/types/models/file';
import { i18n } from '@/i18n/i18n';
import type { FileType } from './types';

// For browsers that do not support the 'accept' input attribute (https://caniuse.com/#feat=input-file-accept)
const validateExtension = (filename: string, validExtensions: string[]) => {
  // if no extensions given then all files are permitted
  if (!validExtensions || validExtensions.length === 0) return true;

  let extension = filename.split('.').pop() as string;

  // Treat extension-less files as having an extension equal to file name
  // e.g. Dockerfile's extension is Dockerfile.
  if (filename.includes('.')) extension = `.${extension}`;

  return validExtensions.map((ext) => ext.toLowerCase()).includes(extension.toLowerCase());
};

type FileSelection = {
  files: FileType[];
  invalidTypeFilenames: string[];
  duplicateFilenames: string[];
};

// getValidFiles splits the Web API File objects into those that match the valid extensions
// and have a name different from the existing files and from each other, and the names
// of those rejected.
// Only the first valid File is kept if the `multiple` option is false.
export const getValidFiles = (
  webApiFileList: File[] | FileList,
  currentFilenames: string[],
  validExtensions: string[],
  multiple = true,
): FileSelection => {
  const selection: FileSelection = { files: [], invalidTypeFilenames: [], duplicateFilenames: [] };

  // Convert Web API FileList to an array of our own file structure.
  // The original File is stored inside `fileObject` for later e.g. uploading, content type detection, hashing, etc.
  // This also helps prevent us adding File objects into the store, which seem to hinder its reactivity(?).
  Array.from(webApiFileList).forEach((fileObject) => {
    const filename = fileObject.name;
    if (!validateExtension(filename, validExtensions)) {
      selection.invalidTypeFilenames.push(filename);
    } else if (
      currentFilenames.includes(filename) ||
      selection.files.some((file) => file.filename === filename)
    ) {
      selection.duplicateFilenames.push(filename);
    } else {
      selection.files.push({
        filename,
        contentType: fileObject.type,
        size: fileObject.size,
        fileObject,
      });
    }
  });

  if (!multiple) selection.files = selection.files.slice(0, 1);
  return selection;
};

// notifyRejectedFiles shows an error toast for each reason getValidFiles rejected files.
export function notifyRejectedFiles({ invalidTypeFilenames, duplicateFilenames }: FileSelection) {
  const toast = useToast();
  const rejections = [
    ['invalidType', invalidTypeFilenames],
    ['duplicate', duplicateFilenames],
  ] as const;
  for (const [reason, filenames] of rejections) {
    if (filenames.length) {
      toast.error(
        i18n.global.t(
          `uploads.rejected.${reason}`,
          { files: formatList(filenames) },
          filenames.length,
        ),
      );
    }
  }
}

// selectFiles returns the files a user picked or dropped that can be added, and tells
// them about any that were rejected.
export function selectFiles(
  webApiFileList: File[] | FileList,
  currentFiles: { filename: string }[],
  validExtensions: string[],
  multiple?: boolean,
) {
  const currentFilenames = currentFiles.map((file) => file.filename);
  const selection = getValidFiles(webApiFileList, currentFilenames, validExtensions, multiple);
  notifyRejectedFiles(selection);
  return selection.files;
}

const claimedDrops = new WeakSet<Event>();

// claimDrop returns true for the first dropzone to see a drop event, and false for any
// it bubbles through after. Dropzones nest (the assessment wizard wraps DocumentsUploader's),
// so this lets the innermost one handle the drop while the outer ones still receive it and
// reset their hover state.
export function claimDrop(event: Event) {
  if (claimedDrops.has(event)) return false;
  claimedDrops.add(event);
  return true;
}

// formatFileSize converts a number of bytes into a friendly size string such as "5 MB".
export const formatFileSize = (sizeInBytes: number) => filesize(sizeInBytes);

// loadFileContent takes a Web API File object and returns its content as a Uint8Array.
export const loadFileContent = (file: File) =>
  new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.result);
    reader.readAsArrayBuffer(file);
  });

// hashFile returns a SHA256 hash of some file content.
// `fileContent` should be a Uint8Array, as outputted by loadFileContent.
// Source: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
// Also helpful: https://luzfcb.github.io/FileAPI-WebCryptoAPI-api-sample/
const hashFile = async (fileContent: Uint8Array) => {
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    // TypeScript update is forcing this type casting and I don't understand the
    // code well enough to do it properly
    // TODO: do it properly
    fileContent as ArrayBufferView<ArrayBuffer>,
  );
  return Array.from(new Uint8Array(hashBuffer));
};

export const hashFileBase64Encoded = async (fileContent: Uint8Array) => {
  const hashArray = await hashFile(fileContent);
  const hashString = String.fromCharCode(...hashArray); // Convert bytes to string
  const base64Hash = btoa(hashString); // Encode string to base64
  return base64Hash;
};

// getFileStatus groups together different upload status booleans (loading/failed)
// with different scan statuses (scanning, approved, rejected), since they are all mutually exclusive.
//
// All these states are grouped into the following states for easy use by components:
// - uploading = file is being uploaded to S3.
// - upload_failed = failed to upload file to S3.
// - scanning = file is uploaded and being scanned.
// - rejected = scan rejected the file e.g. it has a virus or invalid content
// - ready = file was scanned, approved, and is ready for download
//
// Note: We are intentionally vague about file status,
// because the user doesn't need to know the details of why it's ready or was rejected,
// they only need to know that they can or can't download a file,
// with only hints of what may have gone wrong to help us debug.
export const getFileStatus = (file: FileModel) => {
  if (file.uploading) return 'uploading';
  if (file.uploadFailed) return 'upload_failed';

  const scanStatus = file.latestScan?.status;
  if (scanStatus === 'approved') return 'ready';
  if (scanStatus === 'rejected') return 'rejected';
  return 'scanning';
};
